/**
 * Every number a standard check judges against, with why it is that number
 * (IM-200–202).
 *
 * One place, so that "why did this warn?" has one answer to read, and a value
 * changed for one check is visibly a value changed for every check that uses it.
 */
export const THRESHOLDS = {
    /**
     * How far ahead a support deadline starts to warn. Ninety days is one
     * quarter: long enough to plan an upgrade into a customer's change window,
     * which for most of them is booked a month or more out.
     */
    supportWarningDays: 90,
    /**
     * A subnet that can take fewer new nodes than this warns. Five is a modest
     * scale-out — the weekend this epic comes from needed ten — so a subnet
     * under it will refuse the first real burst of load.
     */
    subnetHeadroomNodes: 5,
    /**
     * The VPC CNI's own default for `WARM_PREFIX_TARGET`: each node keeps one
     * spare /28 attached. Used when the setting is not visible to us.
     */
    defaultWarmPrefixTarget: 1,
    /**
     * A change of instance type warns only when it saves at least this much a
     * day. Changing a pool's type replaces every node in it, and under about $30
     * a month that disruption is not worth proposing.
     */
    leverWarnPerDay: 1,
    database: {
        /**
         * Seven-day average CPU. Above 70% a database has no room for the
         * weekly peak it is averaged over; above 90% it is the bottleneck now.
         */
        cpuWarnPercent: 70,
        cpuFailPercent: 90,
        /**
         * Lowest freeable memory, as a share of the class's memory. Postgres
         * leans on the operating system's page cache; below a tenth the cache is
         * being evicted and reads go to storage.
         */
        freeableMemoryWarnRatio: 0.1,
        /**
         * Peak connections against the class's default maximum. Connections
         * past the maximum are refused, and the services retry, so a pool near
         * it fails in bursts rather than steadily.
         */
        connectionsWarnRatio: 0.8,
        connectionsFailRatio: 0.95,
        /**
         * Lowest free storage, as a share of what was provisioned. Below a fifth
         * there is one growth spurt left; below a tenth an import can fill it,
         * and a full volume stops the database accepting writes.
         */
        freeStorageWarnRatio: 0.2,
        freeStorageFailRatio: 0.1,
        /**
         * Postgres's default `max_connections` is `LEAST(memory / 9531392, 5000)`
         * — the engine's own formula on the managed service. A parameter group
         * can override it, which is why the check says "about".
         */
        postgresBytesPerConnection: 9_531_392,
        postgresMaxConnectionsCap: 5000,
    },
    search: {
        /**
         * The cloud's own recommended alarms for a search domain: JVM memory
         * pressure at 80% is when garbage collection starts costing latency, and
         * at 95% the domain blocks writes to protect itself.
         */
        jvmPressureWarnPercent: 80,
        jvmPressureFailPercent: 95,
        /**
         * Free storage per node, as a share of its volume. The recommended alarm
         * is a quarter; the engine's flood-stage watermark blocks writes at 5%
         * free, so a tenth is the last margin before ingestion stops.
         */
        freeStorageWarnRatio: 0.25,
        freeStorageFailRatio: 0.1,
        /** Seven-day average CPU, for the same reason as the database's. */
        cpuWarnPercent: 80,
    },
    /** Inside the cluster (IM-206). */
    workloads: {
        /**
         * A NodePool with room for fewer nodes than this warns. One more node is
         * not a scale-out: a burst that needs two finds the ceiling halfway.
         */
        nodePoolHeadroomWarnNodes: 2,
        /**
         * How long a pod may be Pending before it is a finding. A new node takes
         * one to two minutes to join, so five is past an ordinary scale-out;
         * fifteen is long enough that whatever it waits on is not coming.
         */
        pendingWarnMinutes: 5,
        pendingFailMinutes: 15,
        /**
         * Restarts per hour, per workload, since each pod started. One an hour is
         * a service falling over twice a shift; five is a crash loop the user
         * sees. A pod's own lifetime is counted as at least an hour, so a pod
         * that restarted once in its first minute does not read as sixty.
         */
        restartsWarnPerHour: 1,
        restartsFailPerHour: 5,
        /**
         * Usage this many times the request: the scheduler and the autoscaler
         * place by request, so they cannot see the load. The weekend this epic
         * comes from ran a worker at about ten times its request.
         */
        underRequestedRatio: 3,
        /**
         * A request this many times the usage, and at least the floor below:
         * capacity paid for and held idle. Four, because a request is sized for
         * the peak and a snapshot of usage is not the peak.
         */
        overRequestedRatio: 4,
        /** Below these a gap is noise: 50m against 10m is not worth a sentence. */
        minCpuCores: 0.1,
        minMemoryBytes: 256 * 1024 ** 2,
        /**
         * An over-reserved request is worth raising only above these: half a core
         * or a gibibyte is a node's worth across a few replicas.
         */
        overRequestedMinCpuCores: 0.5,
        overRequestedMinMemoryBytes: 1024 ** 3,
        /**
         * A queue-length target below one message per pod: the autoscaler adds
         * pods for messages it has already handed out, and removes them a poll
         * later. KEDA's own default polling is 30 seconds; under ten, the scaler
         * reacts to each burst before the last one's pods have started.
         */
        minQueueTargetPerPod: 1,
        minPollingSeconds: 10,
        /**
         * Prometheus's memory against its node's allocatable memory, or against
         * its own limit. The kubelet evicts on node pressure before the limit is
         * reached, which is how a Prometheus with a 7Gi limit on an 8 GiB node
         * was evicted five times in three days.
         */
        prometheusMemoryWarnRatio: 0.85,
    },
    /** What the checks over the cluster's own Prometheus judge against (IM-207). */
    prometheus: {
        /**
         * A day's Kong traffic below this many requests is not judged as a ratio:
         * a quiet test instance that answered three of twenty with a 502 while a
         * pod restarted is not an outage, and a ratio over twenty says nothing.
         */
        kongMinRequests: 100,
        /**
         * Server errors as a share of Kong's answers. One in a hundred over the hour
         * or the day is users seeing failures often enough to report them; one in
         * twenty over the hour is something down now.
         */
        kongErrorWarnRatio: 0.01,
        kongErrorFailRatio: 0.05,
    },
    /** Kueue's admission wait, creation to admission (IM-207). */
    queue: {
        /**
         * A job waiting this long for admission is held by its queue rather than
         * starting late. Five minutes is a third of the dataset sync rule's
         * interval: well past anything scheduling alone explains.
         */
        admissionWarnMinutes: 5,
        /**
         * The dataset sync rule runs every fifteen minutes, so a sync still
         * waiting after fifteen has the next one queued behind it: syncs fall
         * further behind with every run, instead of starting late once.
         */
        admissionFailMinutes: 15,
    },
    /** An object whose spec something keeps rewriting (IM-207). */
    rewrites: {
        /**
         * Each spec change adds one to `metadata.generation`, and an object that
         * an apply updates sits in the single or double digits. Ten thousand is
         * past any number of applies; 24/7 NV's chunk-to-ES ScaledObject was at
         * 776,000.
         */
        minGeneration: 10_000,
        /** And this many times its kind's median, so a kind that churns by design is not every item flagged. */
        medianRatio: 100,
    },
};
/** KEDA's polling interval when a ScaledObject sets none. */
export const KEDA_DEFAULT_POLLING_SECONDS = 30;
/**
 * The surcharge for surplus credits, per vCPU-hour, by burstable family.
 *
 * The same in every region and for every size. Checked against the cloud's
 * on-demand pricing page on 2026-10-27, which is also where t4g's rate was
 * found to be $0.04 and not the $0.05 an earlier estimate had used — that is
 * t2, t3 and t3a. It stays a table when the instance prices stopped being one
 * (IM-203): it has not moved since the families launched, and the price list
 * files it under a product family of its own.
 */
export const SURPLUS_CREDIT_RATE = {
    t4g: 0.04,
    t3: 0.05,
    t3a: 0.05,
    t2: 0.05,
};
export const BURSTABLE_SPECS = {
    't4g.large': { vcpus: 2, baseline: 0.3 },
    't4g.xlarge': { vcpus: 4, baseline: 0.4 },
    't4g.2xlarge': { vcpus: 8, baseline: 0.4 },
    't3.large': { vcpus: 2, baseline: 0.3 },
    't3.xlarge': { vcpus: 4, baseline: 0.4 },
    't3.2xlarge': { vcpus: 8, baseline: 0.4 },
    't3a.large': { vcpus: 2, baseline: 0.3 },
    't3a.xlarge': { vcpus: 4, baseline: 0.4 },
    't3a.2xlarge': { vcpus: 8, baseline: 0.4 },
};
/**
 * The general-purpose families a burstable family could move to, size for
 * size: the same vCPUs, memory and processor architecture, at a flat rate.
 *
 * Several, because which is cheapest is the price list's to say, not ours —
 * in `us-east-1` on 2026-10-28 an `m6g.large` was $0.0770 and an `m7g.large`
 * $0.0816. The newer generation is faster per vCPU; whether that buys fewer
 * nodes is a judgement about a workload, which no price can make, so the
 * cheaper type is the one proposed.
 */
export const FIXED_TWIN_FAMILIES = {
    t4g: ['m6g', 'm7g'],
    t3: ['m5', 'm6i', 'm7i'],
    t3a: ['m5a', 'm6a', 'm7a'],
};
/** The family of an instance type: `t4g` of `t4g.large`. */
export const familyOf = (instanceType) => instanceType.split('.')[0] ?? '';
/** Whether a type earns and spends CPU credits. */
export const isBurstable = (instanceType) => SURPLUS_CREDIT_RATE[familyOf(instanceType)] !== undefined;
/**
 * Whether a type is burstable and **throttled** when its credits run out, not
 * charged: Azure's B-series (`Standard_B2ms`, `Standard_B4als_v2`, IM-216).
 * There is no surplus to price, so the surcharge check has nothing to add up —
 * the cost of a B-series node out of credits is a slow node, not a bill.
 */
export const isThrottledBurstable = (instanceType) => /^Standard_B\d/i.test(instanceType);
/** The fixed-performance types a burstable type could move to, before pricing. */
export const twinCandidates = (instanceType) => {
    if (BURSTABLE_SPECS[instanceType] === undefined)
        return [];
    const size = instanceType.slice(instanceType.indexOf('.') + 1);
    return (FIXED_TWIN_FAMILIES[familyOf(instanceType)] ?? []).map((f) => `${f}.${size}`);
};
/** The cheapest priced twin, or undefined when none of them has a price. */
export const twinOf = (instanceType, prices) => {
    let best;
    for (const type of twinCandidates(instanceType)) {
        const hourly = prices[type]?.onDemandHourly;
        if (hourly !== undefined && (best === undefined || hourly < best.hourly)) {
            best = { type, hourly };
        }
    }
    return best;
};
/**
 * Average CPU, in percent, above which a fixed-performance twin is cheaper.
 *
 * `baseline + (twin − burstable) / (vCPUs × rate)`: the burstable type's price
 * plus the surcharge for running above its baseline, against its cheapest
 * twin. Above it, unlimited mode is a premium paid for nothing. Undefined when
 * either price is missing — a break-even is a claim about prices, and is not
 * made without them.
 */
export const breakEvenPercent = (instanceType, prices) => {
    const spec = BURSTABLE_SPECS[instanceType];
    const rate = SURPLUS_CREDIT_RATE[familyOf(instanceType)];
    const own = prices[instanceType]?.onDemandHourly;
    const twin = twinOf(instanceType, prices);
    if (spec === undefined || rate === undefined || own === undefined || twin === undefined) {
        return undefined;
    }
    return (spec.baseline + (twin.hourly - own) / (spec.vcpus * rate)) * 100;
};
/** What a number of surplus credits (vCPU-minutes) cost, in dollars. */
export const surchargeOf = (instanceType, credits) => (credits / 60) * (SURPLUS_CREDIT_RATE[familyOf(instanceType)] ?? 0);
//# sourceMappingURL=thresholds.js.map